'use client';

import type { AdData, Adrop, AdropEvents } from '@adrop/ads-web-sdk';
import { debounce } from 'es-toolkit';
import { useEffect, useMemo, useRef, useState } from 'react';

import { ENV } from '@/constants/env';
import { useDeviceInfoStore, WEBVIEW_TYPE } from '@/stores/DeviceStore';

import { useCustomRouter } from './useCustomRouter';

//* 광고 로드 상태 타입 (SDK AdropEvents 상수에서 유도, import type이라 런타임 import 없음)
type AdEventState = Extract<
  (typeof AdropEvents)[keyof typeof AdropEvents],
  'adReceived' | 'adFailed' | 'adNoFill' | 'adBackfillNoFill'
>;

export type AdLoadState = 'idle' | 'loading' | AdEventState;

export const isAdHidden = (state: AdLoadState) =>
  state === 'adFailed' || state === 'adNoFill';

//* 배너: adNoFill은 backfill 노출 가능 상태, adBackfillNoFill은 backfill도 없음
export const isBannerAdHidden = (state: AdLoadState) =>
  state === 'adFailed' || state === 'adBackfillNoFill';

const AD_DISABLED = false;
export const AD_MOCK_MODE = false;
export const AD_MOCK_DATA = {
  headline: '광고 제목이 들어갑니다',
  body: '광고 설명 텍스트가 들어갑니다',
  asset: 'https://placehold.co/335x81/e2e8f0/64748b?text=AD',
  assetSquare: 'https://placehold.co/64x64/e2e8f0/64748b?text=AD',
};

let initialized = false;

type AdType = 'native' | 'banner';

//* welfarehello.com 또는 localhost 도메인 여부 체크
const isInternalDomain = (url: string) => {
  try {
    const { hostname } = new URL(url);

    return (
      hostname.endsWith('welfarehello.com') ||
      hostname === 'localhost' ||
      hostname.startsWith('localhost:')
    );
  } catch {
    return false;
  }
};

//* destinationURL에서 실제 목적지 URL 추출 (redirect 파라미터)
const extractDestinationUrl = (trackingUrl: string): string => {
  try {
    const url = new URL(trackingUrl);
    const redirect = url.searchParams.get('redirect');

    return redirect ?? trackingUrl;
  } catch {
    return trackingUrl;
  }
};

export const useAd = (unitId: string, options: { type?: AdType } = {}) => {
  const { type = 'native' } = options;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [adState, setAdState] = useState<AdLoadState>('idle');
  const router = useCustomRouter();

  const { isMounted, webviewType, osType } = useDeviceInfoStore((state) => ({
    isMounted: state.isMounted,
    webviewType: state.webviewType,
    osType: state.osType,
  }));
  const isExternalWebview =
    webviewType === WEBVIEW_TYPE.KB_PAY || webviewType === WEBVIEW_TYPE.TOSS;
  //* iOS 웰로 앱에서 에이드랍 광고 로드 시 흰색 웹뷰가 뜨는 이슈로 비활성화
  const isIosWelloApp =
    osType === 'ios' && webviewType === WEBVIEW_TYPE.WELLO_APP;

  //* 상대경로/절대경로 분기 처리 (debounce로 중복 클릭 방지)
  const handleAdClick = useMemo(
    () =>
      debounce(
        (_unit: string, adData: AdData) => {
          const trackingUrl = adData.destinationURL;
          if (!trackingUrl) return;

          //* tracking URL에서 실제 목적지 추출
          const url = extractDestinationUrl(trackingUrl);

          //* 상대경로 (/로 시작) → router.push
          if (url.startsWith('/')) {
            router.push(url);

            return;
          }

          //* 내부 도메인 (*.welfarehello.com, localhost) → pathname 추출 후 router.push
          if (isInternalDomain(url)) {
            const { pathname, search, hash } = new URL(url);
            router.push(pathname + search + hash);

            return;
          }

          //* 외부 절대경로 → 새 창에서 열기
          window.open(url, '_blank');
        },
        300,
        { edges: ['leading'] },
      ),
    [router],
  );

  useEffect(() => {
    //* isMounted가 true가 된 후에만 webviewType 확정됨
    if (!isMounted) return;

    //* 외부 웹뷰(KB Pay, Toss) 또는 iOS 웰로 앱에서는 광고 비활성화 → fallback 표시
    if (isExternalWebview || isIosWelloApp) {
      setAdState('adFailed');

      return;
    }

    if (AD_DISABLED || !containerRef.current) return;

    let cancelled = false;
    let adrop: Adrop | null = null;

    const handlers = {
      onReceived: () => setAdState('adReceived'),
      onFailed: () => setAdState('adFailed'),
      onNoFill: () => setAdState('adNoFill'),
      onBackfillNoFill: () => setAdState('adBackfillNoFill'),
      onClicked: handleAdClick,
    };

    const setup = async () => {
      setAdState('loading');

      try {
        const { Adrop: AdropClass, AdropEvents } =
          await import('@adrop/ads-web-sdk');

        if (cancelled) return;

        if (!initialized) {
          AdropClass.observe({
            appId: ENV.NEXT_PUBLIC_ADROP_APP_ID,
            debug: false,
          });
          initialized = true;
        }

        adrop = AdropClass.instance();

        if (!adrop || cancelled) {
          if (!adrop) setAdState('adFailed');

          return;
        }

        const filter = { unit: unitId };
        adrop.on(AdropEvents.AD_RECEIVED, handlers.onReceived, filter);
        adrop.on(AdropEvents.AD_FAILED, handlers.onFailed, filter);
        adrop.on(AdropEvents.AD_NO_FILL, handlers.onNoFill, filter);
        adrop.on(AdropEvents.AD_CLICKED, handlers.onClicked, filter);

        //* 배너: adNoFill → backfill 노출, adBackfillNoFill → backfill도 없음 → 숨김
        if (type === 'banner') {
          adrop.on(
            AdropEvents.AD_BACKFILL_NO_FILL,
            handlers.onBackfillNoFill,
            filter,
          );
        }

        //* renderAd로 useCustomClick 활성화 (기본값 false이므로 반드시 호출 필요)
        await adrop.renderAd(containerRef.current!, {
          unit: unitId,
          useCustomClick: true,
          //* native 전용 옵션
          ...(type === 'native' && {
            trackMode: 1,
            isEntireClick: true,
          }),
        });
      } catch {
        setAdState('adFailed');
      }
    };

    setup();

    return () => {
      cancelled = true;
      if (adrop) {
        adrop.off('adReceived', handlers.onReceived);
        adrop.off('adFailed', handlers.onFailed);
        adrop.off('adNoFill', handlers.onNoFill);
        adrop.off('adClicked', handlers.onClicked);

        if (type === 'banner') {
          adrop.off('adBackfillNoFill', handlers.onBackfillNoFill);
        }
      }
    };
  }, [
    unitId,
    type,
    isMounted,
    isExternalWebview,
    isIosWelloApp,
    handleAdClick,
  ]);

  return { containerRef, adState };
};
